'use client';

import type { FC } from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from '@wwe-portal/ui/components/carousel';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';
import { Skeleton } from '@wwe-portal/ui/components/skeleton';
import { Layout } from '@wwe-portal/ui/features/subscription';
import { useElementSize } from '@wwe-portal/ui/hooks/use-element-size';
import { cn } from '@wwe-portal/ui/lib/lib';

import type { MilestoneItemProps } from './milestone-item';
import { MilestoneItem } from './milestone-item';

interface MilestoneCarouselProps {
  milestones: Omit<MilestoneItemProps, 'onClickDetails'>[];
  onClickDetails?: (eventId: string, milestoneId: string) => void;
}

export function MilestoneCarouselSkeleton() {
  const [ref, { width }] = useElementSize();
  return (
    <div className="w-full" ref={ref}>
      <Layout
        isRevamp
        items={[{ id: '0' }, { id: '1' }]}
        parentWidth={width}
        renderItem={({ id }) => {
          return (
            <Skeleton className="h-[55px] w-full max-w-[316px]" key={id} />
          );
        }}
      />
    </div>
  );
}

const DesktopCarouselIndicator = () => {
  const { api } = useCarousel();
  const nodes = api?.slideNodes();
  if (!nodes || nodes.length === 1) return null;
  return (
    <ClickableDiv
      className="absolute inset-y-0.5 right-0.5 hidden w-10 items-center justify-center overflow-hidden rounded-md bg-gradient-to-r from-[#00000000] to-[#000000ff] md:flex"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        api?.scrollNext();
      }}
    >
      <div className="flex size-[18px] items-center justify-center overflow-hidden rounded-full bg-[#111010] opacity-60">
        <IcoMoonIcon
          className="size-[10px] -rotate-90 text-white"
          iconName="arrow"
        />
      </div>
    </ClickableDiv>
  );
};

export const MilestoneCarousel: FC<MilestoneCarouselProps> = ({
  milestones,
  onClickDetails,
}) => {
  if (milestones.length === 1) {
    const milestone = milestones[0]!;
    if (!milestone) return null;
    return (
      <div className="w-full">
        <MilestoneItem
          milestone={milestone}
          onClickDetails={() =>
            onClickDetails?.(milestone.eventId, milestone.milestoneId)
          }
        />
      </div>
    );
  }
  const addId = (postfix: string) => (item: MilestoneItemProps) => ({
    ...item,
    key: item.eventId + postfix,
  });
  return (
    <div className="w-full">
      <Carousel
        className="relative w-full overflow-visible md:overflow-x-clip"
        opts={{ loop: true, containScroll: 'trimSnaps' }}
      >
        <CarouselContent
          className={cn('mx-0 ml-0 overflow-visible pl-0 md:mr-0')}
        >
          {[...milestones.map(addId('1')), ...milestones.map(addId('2'))].map(
            (item) => (
              <CarouselItem
                className="basis-[calc(100%-32px)] px-3 md:basis-full md:px-0"
                key={item.key}
              >
                <MilestoneItem
                  isCarousel
                  milestone={item}
                  onClickDetails={() =>
                    onClickDetails?.(item.eventId, item.milestoneId)
                  }
                />
              </CarouselItem>
            )
          )}
        </CarouselContent>
        <DesktopCarouselIndicator />
      </Carousel>
    </div>
  );
};
